import { useState } from 'react'
import { ChevronRight, Smartphone, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { trackEvent } from '../../utils/analytics'
import {
  detectInstallPlatform,
  promptInstall,
  useInstallPrompt,
} from '../../utils/install-prompt'
import { InstallStepsList } from '../pwa/InstallSteps'

const DISMISS_KEY = 'pwa_install_card_dismissal'
// Each press of the cross hides the card for longer: the next day for
// somebody who was just busy, then a week, then a month. The fourth time is a
// no, and the card does not come back.
const DISMISS_DAYS = [1, 7, 30]
const DAY_MS = 24 * 60 * 60 * 1000

interface Dismissal {
  count: number
  /** Epoch ms the card comes back at; null once it never does. */
  until: number | null
}

function readDismissal(): Dismissal {
  try {
    const stored = JSON.parse(localStorage.getItem(DISMISS_KEY) ?? 'null') as Dismissal | null
    if (stored && typeof stored.count === 'number') return stored
  } catch {
    // Unreadable or blocked storage: as if never dismissed.
  }
  return { count: 0, until: 0 }
}

function isDismissed({ until }: Dismissal): boolean {
  return until === null || Date.now() < until
}

function nextDismissal({ count }: Dismissal): Dismissal {
  const days = DISMISS_DAYS[count]
  return { count: count + 1, until: days === undefined ? null : Date.now() + days * DAY_MS }
}

/**
 * "Install the app", at the top of the dashboard.
 *
 * It replaced a toast that people took for the cookie banner and swatted
 * away. A slim card on the landing page is an offer rather than an
 * interruption: tapping it opens the browser's own install dialog where there
 * is one (Android, desktop Chrome and Edge), and otherwise a sheet with the
 * steps for the browser in hand (Safari on iPhone and Mac, Chrome on iPhone).
 *
 * Renders nothing once installed, where the app cannot be installed, or while
 * the cross keeps it away (`DISMISS_DAYS`). On the dashboard it is what the
 * push card falls back to: one offer at a time, and push is the one that
 * brings people back (`PushNudgeCard`).
 */
export function InstallAppCard() {
  const { t } = useTranslation()
  const { prompt, installed } = useInstallPrompt()
  const [platform] = useState(detectInstallPlatform)
  const [dismissed, setDismissed] = useState(() => isDismissed(readDismissal()))
  const [showSteps, setShowSteps] = useState(false)

  if (installed || dismissed || (platform === null && !prompt)) return null

  const install = async () => {
    if (prompt) {
      const outcome = await promptInstall()
      trackEvent(outcome === 'accepted' ? 'pwa_installed' : 'pwa_install_dismissed')
      return
    }
    if (!platform) return
    trackEvent('pwa_install_steps_shown', { platform })
    setShowSteps(true)
  }

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, JSON.stringify(nextDismissal(readDismissal())))
    } catch {
      // Storage blocked: it goes for this visit only.
    }
    setDismissed(true)
  }

  return (
    <>
      <div className="flex items-center gap-1 rounded-xl border bg-card pr-1 text-card-foreground shadow-sm">
        <button
          type="button"
          onClick={install}
          className="flex min-w-0 flex-1 items-center gap-3 rounded-l-xl px-3 py-2.5 text-left transition-colors hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Smartphone className="h-5 w-5" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold">{t('pwa.install.title')}</span>
            <span className="block truncate text-xs text-muted-foreground">
              {t('pwa.install.description')}
            </span>
          </span>
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
        <button
          type="button"
          onClick={dismiss}
          aria-label={t('pwa.install.dismiss')}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <Dialog open={showSteps} onOpenChange={setShowSteps}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('pwa.install.stepsTitle')}</DialogTitle>
            <DialogDescription>{t('pwa.install.stepsDescription')}</DialogDescription>
          </DialogHeader>
          {platform && <InstallStepsList platform={platform} />}
        </DialogContent>
      </Dialog>
    </>
  )
}
